import React, { useState } from 'react';
import { Edit3, ArrowRight, Wand2, Loader2, Upload, ClipboardCheck } from 'lucide-react';
import { SiteContent, DynamicStyle } from '../types';
import { optimizeContentAI } from '../services/geminiService';
import { uploadService } from '../services/uploadService';
import { useWindowSize } from '../hooks/useWindowSize';
import { MediaRenderer } from './MediaRenderer';

interface HeroProps {
  data: SiteContent['hero'];
  isEditable: boolean;
  onUpdate: (data: SiteContent['hero']) => void;
  onNavigate: (view: any) => void;
  styles: Record<string, DynamicStyle>;
  onSelect: (id: string) => void;
  activeId: string | null;
}

const Hero: React.FC<HeroProps> = ({ data, isEditable, onUpdate, onNavigate, styles, onSelect, activeId }) => {
  const [isOptimizing, setIsOptimizing] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { width } = useWindowSize();

  const handleOptimize = async (field: keyof SiteContent['hero']) => {
    setIsOptimizing(field);
    const optimized = await optimizeContentAI(data[field] as string, `Hero section ${field}`);
    onUpdate({ ...data, [field]: optimized });
    setIsOptimizing(null);
  };

  const handleUpload = async () => {
    try {
      setIsUploading(true);
      const base64 = await uploadService.selectAndUpload();
      onUpdate({ ...data, imageUrl: base64 });
    } catch (err) { console.error('Upload failed:', err); }
    finally { setIsUploading(false); }
  };

  const getStyles = (id: string) => {
    const s = styles[id] || {};
    return { 
      fontFamily: s.fontFamily, 
      fontSize: s.fontSize, 
      transform: s.scale ? `scale(${s.scale})` : undefined 
    };
  };

  const dynamicTitleSize = width < 640 ? 'text-5xl' : 
                           width < 768 ? 'text-7xl' : 
                           width < 1024 ? 'text-[8rem]' : 'text-[10rem]';

  return (
    <header id="home" className="relative h-[100dvh] w-full flex items-center justify-center overflow-hidden bg-slate-950" role="banner">
      <div className="absolute inset-0 z-0">
        <MediaRenderer src={data.imageUrl} className="w-full h-full object-cover opacity-70" alt="View of Balut Island" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-transparent to-slate-950/95"></div>
      </div>
      <div className="relative z-10 text-center px-4 w-full max-w-[1400px]">
        {isEditable ? (
          <div className="max-w-3xl mx-auto glass-card p-8 rounded-[2rem] border-white/20">
             <div className="flex items-center gap-2 text-sky-400 mb-6 font-bold justify-center text-[10px] uppercase tracking-luxury"><Edit3 className="w-4 h-4" /> Editorial Desk</div>
             <input value={data.subtitle} onChange={e => onUpdate({...data, subtitle: e.target.value})} className="w-full bg-transparent font-bold tracking-luxury uppercase text-center mb-4 focus:outline-none text-white/50 text-[9px]" aria-label="Subtitle" />
             <textarea value={data.title} onChange={e => onUpdate({...data, title: e.target.value})} className="w-full bg-transparent text-5xl text-white font-serif italic text-center focus:outline-none mb-4" aria-label="Main Title" />
             <div className="flex gap-3 justify-center">
                <button onClick={() => handleOptimize('title')} className="px-6 py-3 bg-white/10 rounded-full text-[10px] font-bold text-white flex items-center gap-2 border border-white/10">{isOptimizing === 'title' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3 text-sky-400" />} Magic Rewrite</button>
                <button onClick={handleUpload} className="px-6 py-3 bg-sky-50 rounded-full text-[10px] font-bold text-white flex items-center gap-2">{isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Media</button>
             </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <span className="font-bold tracking-luxury uppercase text-[8px] md:text-[9px] mb-8 text-white/40 font-sans">{data.subtitle}</span>
            <h1 className="text-white font-serif mb-10 tracking-tighter drop-shadow-2xl px-2 transition-all duration-500" style={getStyles('hero-title')} onClick={() => onSelect('hero-title')}>
              <span className={`${dynamicTitleSize} leading-[0.9] block font-bold`}>{data.title}</span>
            </h1>
            <p className="text-sm md:text-xl text-white/80 mb-14 max-w-2xl font-light leading-relaxed px-6" style={getStyles('hero-desc')} onClick={() => onSelect('hero-desc')}>{data.description}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full px-6 max-w-2xl mx-auto">
              <button onClick={() => onNavigate('discover')} className="w-full sm:w-auto bg-white text-slate-950 px-8 py-4 rounded-full font-bold transition-all hover:bg-sky-500 hover:text-white flex items-center justify-center gap-3 tracking-luxury text-[9px] uppercase shadow-2xl active:scale-95"><span>{data.primaryCTA}</span><ArrowRight className="w-4 h-4" /></button>
              <button onClick={() => onNavigate('register')} className="w-full sm:w-auto bg-sky-500/10 backdrop-blur-md border border-sky-400/30 text-sky-400 px-8 py-4 rounded-full font-bold transition-all hover:bg-sky-500 hover:text-white flex items-center justify-center gap-3 shadow-xl active:scale-95" aria-label="Tourism Registration Form"><ClipboardCheck className="w-4 h-4" /><span className="tracking-luxury text-[9px] uppercase">Book Your Entry</span></button>
            </div>
          </div>
        )}
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 opacity-50"><span className="text-[9px] font-bold tracking-luxury text-white uppercase">Explore</span><div className="w-px h-12 bg-gradient-to-b from-white to-transparent"></div></div>
    </header>
  );
};

export default Hero;